import { useEffect, useState } from 'react';

import css from './Pin.module.css';

/**
 * Las seis casillas y el teclado numérico.
 *
 * Teclado propio y no `<input type="tel">` por dos razones de campo: el teclado
 * del sistema tapa media pantalla y sus teclas son más pequeñas que 62 px, y
 * en iOS el autocompletado se empeña en ofrecer códigos de SMS. Con teclas
 * propias, el tamaño lo decidimos nosotros y no hay sorpresas.
 *
 * No hay `autoFocus` en ningún sitio: en iOS eso abre el teclado del sistema
 * por encima del nuestro.
 */
export function EntradaPin({
    longitud = 6,
    error,
    onCompleto,
    etiquetaBorrar,
}: {
    longitud?: number;
    error?: string | null;
    onCompleto: (pin: string) => void;
    etiquetaBorrar: string;
}) {
    const [valor, setValor] = useState('');
    const [sacude, setSacude] = useState(false);

    // Al llegar un error nuevo se vacía y se sacude. La empleada no tiene que
    // borrar seis dígitos a mano para reintentar.
    useEffect(() => {
        if (error !== null && error !== undefined && error !== '') {
            setValor('');
            setSacude(true);
            const t = setTimeout(() => setSacude(false), 320);

            return () => clearTimeout(t);
        }

        return undefined;
    }, [error]);

    function pulsar(d: string): void {
        if (valor.length >= longitud) {
            return;
        }
        const nuevo = valor + d;
        setValor(nuevo);

        if (nuevo.length === longitud) {
            // Un pelín de espera para que se vea el último punto antes de
            // cambiar de pantalla. Sin esto parece que no se ha registrado.
            setTimeout(() => {
                onCompleto(nuevo);
                // Y se vacía SIEMPRE, no solo al fallar. Este componente no se
                // desmonta entre "elige tu PIN" y "repite el PIN" —es la misma
                // posición del árbol—, así que sin esto las casillas seguirían
                // llenas y los dígitos de la segunda vuelta se ignorarían en
                // silencio. Lo encontró la prueba en un navegador de verdad:
                // la pantalla se quedaba quieta sin ningún error.
                setValor('');
            }, 120);
        }
    }

    return (
        <>
            <div className={`${css.casillas} ${sacude ? css.error : ''}`}>
                {Array.from({ length: longitud }, (_, i) => (
                    <div
                        key={i}
                        className={[
                            css.casilla,
                            i < valor.length ? css.llena : '',
                            i === valor.length ? css.activa : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        {i < valor.length ? '•' : ''}
                    </div>
                ))}
            </div>

            <p className={css.intentos} role="alert">
                {error ?? ''}
            </p>

            <div className={css.teclado}>
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                    <button key={d} className={css.tecla} onClick={() => pulsar(d)}>
                        {d}
                    </button>
                ))}
                <span className={`${css.tecla} ${css.teclaVacia}`} aria-hidden="true" />
                <button className={css.tecla} onClick={() => pulsar('0')}>
                    0
                </button>
                <button
                    className={`${css.tecla} ${css.teclaBorrar}`}
                    onClick={() => setValor(valor.slice(0, -1))}
                    aria-label={etiquetaBorrar}
                >
                    ⌫
                </button>
            </div>
        </>
    );
}
